import {
    LinkPermissionMode,
    type ILayoutLink,
    type ILayoutSubMenu,
} from "../contexts/layouts/LayoutContext";
import {
    GroupIdentity,
    Permission,
    PersonnelIdentity,
    TrialIdentity,
    TrialInstitutionIdentity,
    InstitutionIdentity,
    DepartmentIdentity,
    PatientIdentity,
} from "src/api/Tmd.dtos";
import {
    generalSettingsPermissions,
    hubSpotSettingsPermissions,
    institutionSettingsPermissions,
    itemSettingsPermissions,
    qaSettingsPermissions,
    trialSettingsPermissions,
} from "./redirects";
import AppSettings from "src/AppSettings";
import { spotlight } from "@mantine/spotlight";

export const mainNavigationItems: ILayoutLink[] = [
    {
        icon: AppSettings.icons.dashboard,
        label: "Dashboard",
        href: "/dashboard",
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.DashboardView],
    },
    {
        icon: AppSettings.icons.trial,
        label: "Trials",
        href: "/trials",
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.TrialView, Permission.TrialUpdate],
    },
    {
        icon: AppSettings.icons.institution,
        label: "Institutions",
        href: "/institutions",
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.InstitutionView],
    },
    {
        icon: AppSettings.icons.user,
        label: "Personnel",
        href: "/personnel",
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.PersonnelView],
    },
    {
        icon: AppSettings.icons.group,
        label: "Groups",
        href: "/groups",
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.GroupView],
    },
    {
        icon: AppSettings.icons.settings,
        label: "Settings",
        href: "/settings",
        permissionMode: LinkPermissionMode.Any,
        permissions: [
            Permission.CategoriesView,
            Permission.RolesView,
            Permission.CountriesView,
            Permission.StatesView,
            Permission.ItemComponentTypesView,
            Permission.NoteTypesView,
            Permission.GendersView,
            Permission.InterestsView,
            Permission.MembershipTypesView,
            Permission.ProfessionalDisciplinesView,
            Permission.SalutationsView,
            Permission.TrialCategoriesView,
            Permission.TumorGroupsView,
            Permission.AllocatedWPsView,
            Permission.TrialPhasesView,
            Permission.InvolvementsView,
            Permission.TrialTypesView,
            Permission.SiteLocationsView,
            Permission.FacilityAllianceMembersView,
            Permission.InstitutionTypesView,
            Permission.InstitutionCodingView,
            Permission.FormTemplatesView,
            Permission.FormComponentsView,
            Permission.ReportingTagsView,
            Permission.ReportingTagCategoriesView,
            Permission.ItemComponentsView,
            Permission.OpenClinicaUserRolesView,
            Permission.HubspotContactExportView,
        ],
    },
    {
        icon: AppSettings.icons.search,
        href: "/search",
        actionFn: spotlight.open,
        style: {
            marginLeft: "auto",
        },
        permissionMode: LinkPermissionMode.Any,
        permissions: [
            Permission.TrialView,
            Permission.TrialUpdate,
            Permission.PersonnelView,
            Permission.InstitutionView,
        ],
    },
];

export const createTrialSubMenu = (trialIdentity?: TrialIdentity): ILayoutSubMenu | undefined => {
    if (!trialIdentity) {
        return undefined;
    }

    return {
        code: "trial",
        title: `${trialIdentity.displayNumber} ${trialIdentity.displayTitle}`,
        context: {
            trialId: trialIdentity.id,
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/trials/${trialIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialView],
            },
            {
                icon: undefined,
                label: "Milestones",
                href: `/trials/${trialIdentity.id}/milestones`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialView],
            },
            {
                icon: undefined,
                label: "Institutions",
                href: `/trials/${trialIdentity.id}/institutions`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionView],
            },
            {
                icon: undefined,
                label: "Personnel",
                href: `/trials/${trialIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPersonnelRoleView],
            },
            {
                icon: undefined,
                label: "Participants",
                href: `/trials/${trialIdentity.id}/participants`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPatientView],
            },
            {
                icon: undefined,
                label: "Treatments",
                href: `/trials/${trialIdentity.id}/treatments`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialTreatmentsView],
            },
            {
                icon: undefined,
                label: "Trial Items",
                href: `/trials/${trialIdentity.id}/items`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialItemInstanceView],
            },
            {
                icon: undefined,
                label: "Institution Items",
                href: `/trials/${trialIdentity.id}/trialitems`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialItemView],
            },
            {
                icon: undefined,
                label: "Forms",
                href: `/trials/${trialIdentity.id}/forms`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormView],
            },
            {
                icon: undefined,
                label: "Groups",
                href: `/trials/${trialIdentity.id}/groups`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialGroupView],
            },
            {
                icon: undefined,
                label: "API",
                href: `/trials/${trialIdentity.id}/api`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialApisView],
            },
            {
                icon: undefined,
                label: "Notes",
                href: `/trials/${trialIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialNoteView],
            },
            {
                icon: undefined,
                label: "QA Tracker",
                href: `/trials/${trialIdentity.id}/trackers`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialTrackerView],
            },
        ],
    };
};

export const createTrialInstitutionSubMenu = (
    trialIdentity?: TrialIdentity,
    trialInstitutionIdentity?: TrialInstitutionIdentity,
): ILayoutSubMenu | undefined => {
    if (!trialIdentity || !trialInstitutionIdentity) {
        return undefined;
    }

    return {
        code: "trialInstitution",
        title: trialInstitutionIdentity.institutionName,
        context: {
            trialId: trialIdentity.id,
            institutionId: trialInstitutionIdentity.institutionId,
            trialInstitutionId: trialInstitutionIdentity.id,
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialDetailsView],
            },
            {
                icon: undefined,
                label: "Participants",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/participants`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPatientView],
            },
            {
                icon: undefined,
                label: "Items",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/items`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionItemInstanceView],
            },
            {
                icon: undefined,
                label: "Personnel",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPersonnelRoleView],
            },
            {
                icon: undefined,
                label: "Notes",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialNoteView],
            },
        ],
    };
};

export const createPatientSubMenu = (
    trialIdentity?: TrialIdentity,
    trialInstitutionIdentity?: TrialInstitutionIdentity,
    patientIdentity?: PatientIdentity,
): ILayoutSubMenu | undefined => {
    if (!trialIdentity || !patientIdentity) {
        return undefined;
    }

    const basePath = trialInstitutionIdentity
        ? `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/participants/${patientIdentity.id}`
        : `/trials/${trialIdentity.id}/participants/${patientIdentity.id}`;

    return {
        code: "patient",
        title: patientIdentity.registrationNumber,
        context: {
            trialId: trialIdentity.id,
            institutionId: trialInstitutionIdentity?.institutionId ?? patientIdentity.institutionId,
            trialInstitutionId: trialInstitutionIdentity?.id ?? patientIdentity.trialInstitutionId,
            patientId: patientIdentity.id,
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `${basePath}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPatientView],
            },
            {
                icon: undefined,
                label: "Administration Case Report",
                href: `${basePath}/administrationcasereport`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormView],
            },
        ],
    };
};

export const createInstitutionSubMenu = (
    institutionIdentity?: InstitutionIdentity,
): ILayoutSubMenu | undefined => {
    if (!institutionIdentity) {
        return undefined;
    }

    return {
        code: "institution",
        title: institutionIdentity.name,
        context: {
            institutionId: institutionIdentity.id,
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/institutions/${institutionIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionDetailsView],
            },
            {
                icon: undefined,
                label: "Departments",
                href: `/institutions/${institutionIdentity.id}/departments`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionDepartmentView],
            },
            {
                icon: undefined,
                label: "Personnel",
                href: `/institutions/${institutionIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionPersonnelView],
            },
            {
                icon: undefined,
                label: "Forms",
                href: `/institutions/${institutionIdentity.id}/forms`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionFormView],
            },
            {
                icon: undefined,
                label: "Notes",
                href: `/institutions/${institutionIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionNoteView],
            },
        ],
    };
};

export const createDepartmentSubMenu = (
    institutionIdentity?: InstitutionIdentity,
    departmentIdentity?: DepartmentIdentity,
): ILayoutSubMenu | undefined => {
    if (!institutionIdentity || !departmentIdentity) {
        return undefined;
    }

    return {
        code: "department",
        title: departmentIdentity.name,
        context: {
            institutionId: departmentIdentity.institutionId,
            departmentId: departmentIdentity.id,
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/institutions/${departmentIdentity.institutionId}/departments/${departmentIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionDepartmentView],
            },
            {
                icon: undefined,
                label: "Personnel",
                href: `/institutions/${departmentIdentity.institutionId}/departments/${departmentIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionDepartmentView],
            },
        ],
    };
};

export const createPersonnelSubMenu = (
    personnelIdentity?: PersonnelIdentity,
): ILayoutSubMenu | undefined => {
    if (!personnelIdentity) {
        return undefined;
    }

    return {
        code: "personnel",
        title: `${personnelIdentity.firstName} ${personnelIdentity.lastName}`,
        context: {
            personnelId: personnelIdentity.id,
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/personnel/${personnelIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelView],
            },
            {
                icon: undefined,
                label: "Items",
                href: `/personnel/${personnelIdentity.id}/items`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelItemsView],
            },
            {
                icon: undefined,
                label: "HubSpot",
                href: `/personnel/${personnelIdentity.id}/hubspot`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelHubspotView],
            },
            {
                icon: undefined,
                label: "Institutions & Roles",
                href: `/personnel/${personnelIdentity.id}/roles`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelRoleView],
            },
            {
                icon: undefined,
                label: "Permissions",
                href: `/personnel/${personnelIdentity.id}/permissions`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelRoleView],
            },
            {
                icon: undefined,
                label: "Notes",
                href: `/personnel/${personnelIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelNoteView],
            },
            {
                icon: undefined,
                label: "Login & Security",
                href: `/personnel/${personnelIdentity.id}/activedirectory`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelSecurityView],
            },
            {
                icon: undefined,
                label: "OpenClinica",
                href: `/personnel/${personnelIdentity.id}/openclinica`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelOpenClinicaView],
            },
        ],
    };
};

export const createGroupSubMenu = (groupIdentity?: GroupIdentity): ILayoutSubMenu | undefined => {
    if (!groupIdentity) {
        return undefined;
    }

    return {
        code: "group",
        title: groupIdentity.name,
        context: {
            groupId: groupIdentity.id,
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/groups/${groupIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.GroupView],
            },
            {
                icon: undefined,
                label: "Items",
                href: `/groups/${groupIdentity.id}/groupitems`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.GroupItemsView],
            },
            {
                icon: undefined,
                label: "Personnel",
                href: `/groups/${groupIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.GroupPersonnelRoleView],
            },
            {
                icon: undefined,
                label: "Trials",
                href: `/groups/${groupIdentity.id}/trials`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialView],
            },
            {
                icon: undefined,
                label: "Notes",
                href: `/groups/${groupIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.GroupNotesView],
            },
        ],
    };
};

export const createSettingsSubMenu = (): ILayoutSubMenu => {
    return {
        code: "settings",
        title: undefined,
        items: [
            {
                icon: undefined,
                label: "General",
                href: `/settings/general`,
                permissionMode: LinkPermissionMode.Any,
                permissions: generalSettingsPermissions,
            },
            {
                icon: undefined,
                label: "Trials",
                href: `/settings/trial`,
                permissionMode: LinkPermissionMode.Any,
                permissions: trialSettingsPermissions,
            },
            {
                icon: undefined,
                label: "Items",
                href: `/settings/item`,
                permissionMode: LinkPermissionMode.Any,
                permissions: itemSettingsPermissions,
            },
            {
                icon: undefined,
                label: "Institutions",
                href: `/settings/institution`,
                permissionMode: LinkPermissionMode.Any,
                permissions: institutionSettingsPermissions,
            },
            {
                icon: undefined,
                label: "QA",
                href: `/settings/qa`,
                permissionMode: LinkPermissionMode.Any,
                permissions: qaSettingsPermissions,
            },
            {
                icon: undefined,
                label: "OpenClinica User Roles",
                href: `/settings/openclinicauserroles`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.OpenClinicaUserRolesView],
            },
            {
                icon: undefined,
                label: "HubSpot",
                href: `/settings/hubspot`,
                permissionMode: LinkPermissionMode.Any,
                permissions: hubSpotSettingsPermissions,
            },
        ],
    };
};

export const createGeneralSettingsSubMenu = (): ILayoutSubMenu => {
    return {
        code: "generalsettings",
        title: undefined,
        items: [
            {
                icon: undefined,
                label: "Roles",
                href: `/settings/general/roles`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.RolesView],
            },
            {
                icon: undefined,
                label: "Countries",
                href: `/settings/general/countries`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.CountriesView],
            },
            {
                icon: undefined,
                label: "States",
                href: `/settings/general/states`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.StatesView],
            },
            {
                icon: undefined,
                label: "Item Component Types",
                href: `/settings/general/itemcomponenttypes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.ItemComponentTypesView],
            },
            {
                icon: undefined,
                label: "Note Types",
                href: `/settings/general/notetypes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.NoteTypesView],
            },
            {
                icon: undefined,
                label: "Genders",
                href: `/settings/general/genders`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.GendersView],
            },
            {
                icon: undefined,
                label: "Interests",
                href: `/settings/general/interests`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InterestsView],
            },
            {
                icon: undefined,
                label: "Membership Types",
                href: `/settings/general/membershiptypes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.MembershipTypesView],
            },
            {
                icon: undefined,
                label: "Professional Disciplines",
                href: `/settings/general/professionaldisciplines`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.ProfessionalDisciplinesView],
            },
            {
                icon: undefined,
                label: "Salutations",
                href: `/settings/general/salutations`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.SalutationsView],
            },
        ],
    };
};

export const createTrialSettingsSubMenu = (): ILayoutSubMenu => {
    return {
        code: "trialsettings",
        title: undefined,
        items: [
            {
                icon: undefined,
                label: "Trial Categories",
                href: `/settings/trial/trialcategorys`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialCategoriesView],
            },
            {
                icon: undefined,
                label: "Tumour Groups",
                href: `/settings/trial/tumourgroups`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TumorGroupsView],
            },
            {
                icon: undefined,
                label: "Allocated WPs",
                href: `/settings/trial/allocatedwps`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.AllocatedWPsView],
            },
            {
                icon: undefined,
                label: "Trial Phases",
                href: `/settings/trial/trialphases`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPhasesView],
            },
            {
                icon: undefined,
                label: "Involvement",
                href: `/settings/trial/involvements`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InvolvementsView],
            },
            {
                icon: undefined,
                label: "Analysis Types",
                href: `/settings/trial/trialAnalysisTypes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialAnalysisTypesView],
            },
            {
                icon: undefined,
                label: "Presentation Types",
                href: `/settings/trial/trialPresentationTypes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPresentationTypesView],
            },
            {
                icon: undefined,
                label: "Publication Types",
                href: `/settings/trial/trialPublicationTypes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPublicationTypesView],
            },
        ],
    };
};

export const createItemSettingsSubMenu = (): ILayoutSubMenu => {
    return {
        code: "itemsettings",
        title: undefined,
        items: [
            {
                icon: undefined,
                label: "Trial Item Templates",
                href: "/settings/item/trialitemtemplates",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TemplateTrialItemsView],
            },
            {
                icon: undefined,
                label: "Trial Institution Item Templates",
                href: "/settings/item/itemtemplates",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionItemTemplatesView],
            },
            {
                icon: undefined,
                label: "Item Components",
                href: "/settings/item/itemcomponents",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.ItemComponentsView],
            },
            {
                icon: undefined,
                label: "Item Categories",
                href: "/settings/item/itemcategories",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.CategoriesView],
            },
        ],
    };
};

export const createInstitutionSettingsSubMenu = (): ILayoutSubMenu => {
    return {
        code: "institutionsettings",
        title: undefined,
        items: [
            {
                icon: undefined,
                label: "Site Locations",
                href: "/settings/institution/sitelocations",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.SiteLocationsView],
            },
            {
                icon: undefined,
                label: "Facility Alliance Members",
                href: "/settings/institution/facilityalliancemembers",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.FacilityAllianceMembersView],
            },
            {
                icon: undefined,
                label: "Institution Types",
                href: "/settings/institution/institutiontypes",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionTypesView],
            }, //,
            // {
            //     icon: undefined,
            //     label: "Institution Coding",
            //     href: "/settings/institution/institutioncodings",
            //     permissionMode: LinkPermissionMode.Any,
            //     permissions: [
            //         Permission.InstitutionCodingView
            //     ]
            // }
        ],
    };
};

export const createQaSettingsSubMenu = (): ILayoutSubMenu => {
    return {
        code: "qasettings",
        title: undefined,
        items: [
            {
                icon: undefined,
                label: "Form Templates",
                href: "/settings/qa/formtemplates",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.FormTemplatesView],
            },
            {
                icon: undefined,
                label: "Form Components",
                href: "/settings/qa/formcomponents",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.FormComponentsView],
            },
            {
                icon: undefined,
                label: "Reporting Tags",
                href: "/settings/qa/reportingtags",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.ReportingTagsView],
            },
            {
                icon: undefined,
                label: "Reporting Tag Categories",
                href: "/settings/qa/reportingtagcategories",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.ReportingTagCategoriesView],
            },
            {
                icon: undefined,
                label: "Task Types",
                href: "/settings/qa/tasktypes",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TaskTypesView],
            },
            {
                icon: undefined,
                label: "Tracker Types",
                href: "/settings/qa/trackertypes",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TaskTypesView],
            },
            {
                icon: undefined,
                label: "Tracker Milestones",
                href: "/settings/qa/trackermilestones",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TaskTypesView],
            },
            {
                icon: undefined,
                label: "File Types",
                href: "/settings/qa/formtemplatefiletypes",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.FormTemplateFileTypesView],
            },
            {
                icon: undefined,
                label: "QA Email Templates",
                href: "/settings/qa/qaEmailTemplates",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.QAEmailTemplatesView],
            },
        ],
    };
};

export const createHubspotSettingsSubMenu = (): ILayoutSubMenu => {
    return {
        code: "hubspotsettings",
        title: undefined,
        items: [
            {
                icon: undefined,
                label: "Active Members",
                href: "/settings/hubspot/activemembers",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.HubspotContactExportView],
            },
            {
                icon: undefined,
                label: "Email Maintenance",
                href: "/settings/hubspot/emailupdate",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.HubspotContactExportView],
            },
            {
                icon: undefined,
                label: "Process new members",
                href: "/settings/hubspot/import",
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.HubspotContactExportView],
            },
        ],
    };
};
